import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSession } from '@/hooks/useSession'
import { LogoutButton } from '@/components/ui/LogoutButton'
import { LangSwitcher } from '@/components/ui/LangSwitcher'
import { useLockStatus, formatCountdown } from '@/hooks/useLockStatus'
import { useLocalizedTeamName } from '@/utils/teamNames'
import { fetchBracket, fetchMyPredictions, submitPredictions } from '@/api/bracket'
import { BracketTree } from '@/components/bracket/BracketTree'
import { Spinner } from '@/components/ui/Spinner'
import { useToast } from '@/components/ui/Toast'
import type { Bracket, Prediction, PredictionInput } from '@/types/bracket'

export function BracketPage() {
  const { t } = useTranslation()
  const session = useSession()
  const { isLocked, secondsLeft, activeRound, rounds } = useLockStatus()
  const getTeamName = useLocalizedTeamName()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [bracket, setBracket] = useState<Bracket | null>(null)
  const [predictions, setPredictions] = useState<Prediction[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!session.token) {
      navigate('/join')
      return
    }
    loadData()
  }, [session.token])

  // Poll for updates every 60s
  useEffect(() => {
    const id = setInterval(loadData, 60_000)
    return () => clearInterval(id)
  }, [session.token])

  async function loadData() {
    if (!session.token) return
    try {
      const [b, p] = await Promise.all([
        fetchBracket(session.token),
        fetchMyPredictions(session.token),
      ])
      setBracket(b)
      setPredictions(p)
    } catch {
      // silent — don't disrupt the UI on polling errors
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = useCallback(async (inputs: PredictionInput[]) => {
    if (!session.token) return
    setSubmitting(true)
    try {
      const result = await submitPredictions(inputs, session.token)
      toast(`Saved ${result.saved} predictions!`, 'success')
      await loadData()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save'
      toast(msg, 'error')
    } finally {
      setSubmitting(false)
    }
  }, [session.token, toast])

  const MATCH_LOCK_BUFFER_SECS = 30 * 60

  // Find the next match to lock and how long until it does (across all non-SCORED rounds)
  const nextLockInfo = useMemo(() => {
    if (!bracket) return null
    const now = Math.floor(Date.now() / 1000)
    const scoredRounds = new Set(rounds.filter(r => r.status === 'SCORED').map(r => r.round))
    const matches = bracket.rounds
      .filter(r => !scoredRounds.has(r.round))
      .flatMap(r => r.matches)
    const upcoming = matches
      .filter(m => m.scheduled_at && m.status === 'SCHEDULED' && m.scheduled_at - MATCH_LOCK_BUFFER_SECS > now)
      .sort((a, b) => a.scheduled_at! - b.scheduled_at!)
    const next = upcoming[0] ?? null
    if (!next) return null
    return {
      match: next,
      secsUntilLock: next.scheduled_at! - MATCH_LOCK_BUFFER_SECS - now,
    }
  }, [bracket, activeRound, secondsLeft]) // secondsLeft as a tick proxy

  const r32Matches = bracket?.rounds.find(r => r.round === 'R32')?.matches ?? []
  const hasPreliminary = r32Matches.some(
    m => (m.home_team_id || m.away_team_id) && m.teams_confirmed === 0
  )
  const allR32Confirmed = r32Matches.length > 0 &&
    r32Matches.every(m => m.home_team_id && m.away_team_id && m.teams_confirmed === 1)

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="border-b border-white/10 bg-pitch-900/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-[1800px] mx-auto px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🏆</span>
            <div>
              <h1 className="text-base font-black text-white leading-tight">{t('bracket.title')}</h1>
              <p className="text-[11px] text-white/40">{t('bracket.subtitle')}</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Bracket status */}
            {hasPreliminary && (
              <div className="hidden sm:flex items-center gap-1.5 text-[11px] font-medium text-amber-400 bg-amber-400/10 border border-amber-400/20 px-2.5 py-1 rounded-full">
                <span>~</span>
                <span>{t('bracket.preliminaryBadge')}</span>
              </div>
            )}
            {allR32Confirmed && (
              <div className="hidden sm:flex items-center gap-1.5 text-[11px] font-medium text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-2.5 py-1 rounded-full">
                <span>✓</span>
                <span>{t('bracket.confirmedBadge')}</span>
              </div>
            )}

            {/* Countdown — per-match: shows next upcoming match and time until it locks */}
            {!isLocked ? (
              nextLockInfo ? (
                <div className="text-center">
                  <div className="text-[10px] text-white/40 mb-0.5">
                    {nextLockInfo.match.home_team && nextLockInfo.match.away_team
                      ? `${nextLockInfo.match.home_team.flag_emoji ?? ''} ${getTeamName(nextLockInfo.match.home_team.id, nextLockInfo.match.home_team.name)} vs ${nextLockInfo.match.away_team.flag_emoji ?? ''} ${getTeamName(nextLockInfo.match.away_team.id, nextLockInfo.match.away_team.name)}`
                      : t('bracket.nextLockIn')}
                  </div>
                  <div className="text-sm font-bold text-gold-400 tabular-nums">
                    {t('bracket.nextLockIn')} {formatCountdown(nextLockInfo.secsUntilLock)}
                  </div>
                </div>
              ) : (
                <div className="text-xs text-white/40">{t('bracket.open')}</div>
              )
            ) : (
              <div className="badge-finished">{t('bracket.locked')}</div>
            )}

            <nav className="flex gap-2 items-center">
              <LangSwitcher />
              <a href="/standings" className="btn-secondary btn-sm text-sm">📊 {t('standings.nav')}</a>
              <a href="/champion" className="btn-secondary btn-sm text-sm">🏆 {t('nav.champion')}</a>
              <a href="/leaderboard" className="btn-secondary btn-sm text-sm">🏅 {t('nav.leaderboard')}</a>
              {session.isAdmin && (
                <a href="/admin" className="btn-secondary btn-sm text-sm">⚙️ {t('nav.admin')}</a>
              )}
            </nav>

            <div className="flex items-center gap-2">
              <span className="text-sm text-emerald-400 font-semibold">{session.displayName}</span>
              <LogoutButton />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1800px] mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-32">
            <Spinner size="lg" />
          </div>
        ) : !bracket ? (
          <div className="text-center py-32 text-white/40">
            <div className="text-5xl mb-4">⚽</div>
            <p className="text-lg font-medium">{t('bracket.notAvailable')}</p>
            <p className="text-sm mt-2">{t('bracket.notAvailableDesc')}</p>
          </div>
        ) : (
          <BracketTree
            bracket={bracket}
            predictions={predictions}
            isLocked={isLocked}
            activeRound={activeRound}
            rounds={rounds}
            onSubmit={handleSubmit}
            isSubmitting={submitting}
          />
        )}
      </div>
    </div>
  )
}
